import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  Hash,
  Minus,
  Plus,
  Tag,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Users,
  DollarSign,
  ArrowRightLeft,
  Package,
  Clock,
  XCircle,
  RotateCcw,
  Info,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { api } from '../../lib/api';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import {
  type AvailablePlan,
  type PlanPrice,
  type TierItem,
  formatCents,
  intervalLabel,
  intervalLabelFull,
  calculateTieredTotal,
  type CouponDetails,
  describeCouponDiscount,
  describeCouponDuration,
  applyCouponToCents,
  couponExcludesProduct,
} from '../../lib/billing';

// ── Types ──────────────────────────────────────────────────────────────────

interface StripeData {
  customerId?: string;
  subscriptionId?: string;
  status?: string;
  subscriptionQuantity?: number;
  priceId?: string;
  productId?: string;
  productName?: string;
  priceNickname?: string;
  lastPaymentFailed?: boolean;
  trialEnd?: number | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: number | null;
}

interface PaymentMethodData {
  id: string;
  type: string;
  card: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  } | null;
}

interface MemberCountData {
  activeCount: number;
  invitedCount: number;
  totalCount: number;
}

interface PriceDetails {
  unitAmount: number;
  currency: string;
  interval: string;
  billingScheme: string;
  nickname: string | null;
  isFlatRate: boolean;
  tiersMode: string | null;
  tiers: TierItem[] | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Return the number of days remaining until a Unix timestamp (seconds). */
function daysUntil(unixSeconds: number): number {
  const ms = unixSeconds * 1000 - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/** Human-readable labels for plan feature keys. */
const FEATURE_LABELS: Record<string, string> = {
  commercialMovingQuotes: 'Commercial Moving Quotes',
  residentialMovingQuotes: 'Residential Moving Quotes',
  dispatch: 'Dispatch',
  jobs: 'Jobs',
  leadPipeline: 'Lead Pipeline',
  clientPortal: 'Client Portal',
  onlineLeadCapture: 'Online Lead Capture',
  quotePdfs: 'Quote PDFs',
  fleetManagement: 'Fleet Management',
  customerPayments: 'Customer Payments',
  smartMovingIntegration: 'SmartMoving Integration',
  stripeConnect: 'Stripe Connect',
};

// ── Main page component ────────────────────────────────────────────────────

export function ManagePlanPage() {
  const navigate = useNavigate();
  const { currentUser, adminOrg, stripeMode, refreshMemberships } = useUserProfile();

  // Subscription data from backend
  const [billing, setBilling] = useState<StripeData | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(true);

  // User quantity state
  const [seats, setSeats] = useState(1);
  const [seatsSaving, setSeatsSaving] = useState(false);
  const [seatsSuccess, setSeatsSuccess] = useState(false);
  const [seatsError, setSeatsError] = useState<string | null>(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponSaving, setCouponSaving] = useState(false);
  const [couponResult, setCouponResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Checkout state


  // Available plans (from Stripe)
  const [plans, setPlans] = useState<AvailablePlan[]>([]);

  // Current subscription's price details
  const [currentPrice, setCurrentPrice] = useState<PriceDetails | null>(null);

  // Selected billing interval for new subscriptions
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>('month');

  // Payment method state (for banner)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodData | null | undefined>(undefined);

  // Org member count (for seat floor)
  const [memberCount, setMemberCount] = useState<MemberCountData | null>(null);

  // Plan switching state
  const [switchingPrice, setSwitchingPrice] = useState(false);
  const [switchSuccess, setSwitchSuccess] = useState(false);



  // Confirmation modal states
  const [confirmSeats, setConfirmSeats] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Cancel / reactivate state
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Choose New Plan modal
  const [viewPlanDetail, setViewPlanDetail] = useState<AvailablePlan | null>(null);
  const [modalInterval, setModalInterval] = useState<'month' | 'year'>('month');
  const [confirmNewPlan, setConfirmNewPlan] = useState(false);
  const [switchingPlan, setSwitchingPlan] = useState(false);
  // Coupon entered in the plan modal before checkout (new subscribers only)
  const [modalCouponCode, setModalCouponCode] = useState('');
  const [modalCouponSaving, setModalCouponSaving] = useState(false);
  const [modalCouponResult, setModalCouponResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [modalAppliedCoupon, setModalAppliedCoupon] = useState<CouponDetails | null>(null);

  // Track whether critical Stripe data failed to load
  const [loadError, setLoadError] = useState<string | null>(null);

  // Selected product for new subscriptions (when multiple products available)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // Test card info (only loaded for demo orgs)
  const [testCardNumber, setTestCardNumber] = useState<string | null>(null);
  const isDemoOrg = stripeMode === 'test';

  useEffect(() => {
    if (!isDemoOrg) return;
    let cancelled = false;
    api.get<{ success: boolean; data: { cardNumber: string } | null }>('/billing/test-card')
      .then((res) => { if (!cancelled && res.data) setTestCardNumber(res.data.cardNumber); })
      .catch(() => { /* ignore — test card is informational only */ });
    return () => { cancelled = true; };
  }, [isDemoOrg]);

  const hasSubscription = !!billing?.subscriptionId;

  // Reusable data loader
  const reloadAllData = useCallback(async () => {
    setLoadingBilling(true);
    setLoadError(null);
    try {
      const [subRes, priceRes, plansRes, pmRes, memberRes] = await Promise.allSettled([
        api.get<{ success: boolean; data: StripeData | null }>('/billing/subscription'),
        api.get<{ success: boolean; data: PriceDetails }>('/billing/price'),
        api.get<{ success: boolean; data: AvailablePlan[] }>('/billing/available-plans'),
        api.get<{ success: boolean; data: PaymentMethodData | null }>('/billing/payment-method'),
        api.get<{ success: boolean; data: MemberCountData }>('/billing/org-member-count'),
      ]);

      if (subRes.status === 'fulfilled' && subRes.value.data) {
        setBilling(subRes.value.data);
        if (subRes.value.data.subscriptionQuantity) setSeats(subRes.value.data.subscriptionQuantity);
      }
      if (priceRes.status === 'fulfilled') setCurrentPrice(priceRes.value.data);
      if (plansRes.status === 'fulfilled') setPlans(plansRes.value.data);
      if (pmRes.status === 'fulfilled') setPaymentMethod(pmRes.value.data);
      else setPaymentMethod(null);
      if (memberRes.status === 'fulfilled') setMemberCount(memberRes.value.data);

      // Determine which failures are critical.
      // Plans are always critical (needed to show pricing for new and existing).
      // Subscription is always critical (need to know current state).
      // Price is only critical when the org HAS a subscription — for new
      // subscribers the available-plans endpoint provides pricing instead.
      const hasExistingSub =
        subRes.status === 'fulfilled' && !!subRes.value.data?.subscriptionId;
      const criticalResults = hasExistingSub
        ? [subRes, priceRes, plansRes]
        : [subRes, plansRes];
      const criticalFailures = criticalResults.filter(
        (r) => r.status === 'rejected',
      );
      if (criticalFailures.length > 0) {
        setLoadError(
          'Unable to load billing data from Stripe. Some controls are disabled until this is resolved.',
        );
      }
    } finally {
      setLoadingBilling(false);
    }
  }, []);

  // Load all data on mount
  useEffect(() => {
    reloadAllData();
  }, [reloadAllData]);

  // Guard: Platform Admin or Org Admin
  if (!currentUser?.isPlatformAdmin && !adminOrg) {
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-2xl md:mx-auto">
        <p className="text-small text-slate text-center py-16">
          You don't have access to plan management.
        </p>
      </div>
    );
  }

  // True when critical Stripe data failed to load — disables all interactive controls
  const dataUnavailable = !!loadError;

  // Detect flat-rate (non-seat-based) plan
  const isFlatRate = !!(
    currentPrice?.isFlatRate ||
    billing?.priceNickname?.toLowerCase().includes('flat')
  );

  const seatFloor = memberCount ? memberCount.totalCount : 1;
  const canDecrement = !dataUnavailable && seats > Math.max(1, seatFloor);
  const isBelowFloor = seats < seatFloor;

  // Derive available prices for the selected interval
  const allPrices = plans.flatMap((p) => p.prices);
  const pricesForInterval = allPrices.filter((p) => p.interval === selectedInterval);

  // For new subscriptions: selected product and its price
  const selectedPlan = plans.find((p) => p.id === selectedProductId) ?? plans[0] ?? null;
  const selectedPlanPrices = selectedPlan?.prices.filter((p) => p.interval === selectedInterval) ?? [];
  const selectedPrice = selectedPlanPrices[0] ?? pricesForInterval[0] ?? null;
  // For subscriptions: find the alternative interval price to switch to
  const currentInterval = currentPrice?.interval ?? 'month';
  const alternativeInterval = currentInterval === 'month' ? 'year' : 'month';
  // Find alternative price within the same product
  const currentProduct = plans.find((p) => p.id === billing?.productId);
  const alternativePrice = currentProduct?.prices.find((p) => p.interval === alternativeInterval)
    ?? allPrices.find((p) => p.interval === alternativeInterval) ?? null;

  // Calculate display amounts
  const getEstimatedTotal = (price: PlanPrice | PriceDetails | null, qty: number): number | null => {
    if (!price) return null;
    if (price.billingScheme === 'tiered' && price.tiers) {
      return calculateTieredTotal(price.tiers, qty, price.tiersMode) / 100;
    }
    if (price.unitAmount !== null) {
      return (price.isFlatRate ? 1 : qty) * price.unitAmount / 100;
    }
    return null;
  };

  const getPricePerSeat = (price: PlanPrice | PriceDetails | null): number | null => {
    if (!price) return null;
    if (price.billingScheme === 'tiered') return null;
    if (price.isFlatRate) return null;
    if (price.unitAmount !== null) return price.unitAmount / 100;
    return null;
  };

  const handleDecrementSeats = () => {
    setSeats((s) => Math.max(seatFloor, s - 1));
  };
  const handleIncrementSeats = () => setSeats((s) => s + 1);

  const handleSaveSeats = async () => {
    if (seatsSaving) return;
    setSeatsSaving(true);
    setSeatsSuccess(false);
    setSeatsError(null);

    try {
      await api.patch<{ success: boolean }>('/billing/seats', { quantity: seats });
      setSeatsSuccess(true);
      await reloadAllData();
      setTimeout(() => setSeatsSuccess(false), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update user count.';
      setSeatsError(message);
    } finally {
      setSeatsSaving(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setCouponSaving(true);
    setCouponResult(null);

    try {
      const res = await api.post<{
        success: boolean;
        data: { couponName: string; percentOff?: number; amountOff?: number };
      }>('/billing/coupon', { code: couponCode.trim() });

      const d = res.data;
      const discount = d.percentOff
        ? `${d.percentOff}% off`
        : d.amountOff
          ? `$${(d.amountOff / 100).toFixed(2)} off`
          : '';

      setCouponResult({
        ok: true,
        message: `Coupon "${d.couponName}" applied! ${discount}`.trim(),
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid coupon.';
      setCouponResult({ ok: false, message });
    } finally {
      setCouponSaving(false);
    }
  };

  // Clear any coupon entered in the plan modal whenever it opens for a different plan or closes.
  useEffect(() => {
    setModalCouponCode('');
    setModalCouponSaving(false);
    setModalCouponResult(null);
    setModalAppliedCoupon(null);
  }, [viewPlanDetail?.id]);

  /** Validate the promo code typed in the plan modal (does not apply it; checkout does). */
  const handleValidateModalCoupon = async () => {
    const code = modalCouponCode.trim();
    if (!code) return;

    setModalCouponSaving(true);
    setModalCouponResult(null);

    try {
      const res = await api.post<{ success: boolean; data: CouponDetails }>(
        '/billing/validate-coupon',
        { code },
      );
      const d = res.data;
      setModalAppliedCoupon(d);
      setModalCouponResult({
        ok: true,
        message: `Coupon "${d.couponName}" accepted: ${describeCouponDiscount(d)} ${describeCouponDuration(d)}.`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid coupon.';
      setModalCouponResult({ ok: false, message });
      setModalAppliedCoupon(null);
    } finally {
      setModalCouponSaving(false);
    }
  };

  const handleStartCheckout = (overridePriceId?: string, couponCodeForCheckout?: string) => {
    const priceId = overridePriceId ?? selectedPrice?.id;
    if (!priceId) {
      setSeatsError('No pricing available. Ask your IOMI admin to configure pricing.');
      return;
    }

    navigate('/checkout', {
      state: {
        priceId,
        seats,
        ...(couponCodeForCheckout ? { couponCode: couponCodeForCheckout } : {}),
        returnTo: '/admin/company/billing/plan',
      },
    });
  };

  const handleSwitchPrice = async () => {
    if (!alternativePrice || switchingPrice) return;

    setSwitchingPrice(true);
    setSwitchSuccess(false);
    setSeatsError(null);

    try {
      await api.patch<{ success: boolean }>('/billing/switch-price', {
        priceId: alternativePrice.id,
      });
      await reloadAllData();
      await refreshMemberships();
      setSwitchSuccess(true);
      setTimeout(() => setSwitchSuccess(false), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to switch plan.';
      setSeatsError(message);
    } finally {
      setSwitchingPrice(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (canceling) return;
    setCanceling(true);
    setSeatsError(null);
    try {
      await api.post<{ success: boolean }>('/billing/cancel-subscription');
      await reloadAllData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to cancel subscription.';
      setSeatsError(message);
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (reactivating) return;
    setReactivating(true);
    setSeatsError(null);
    try {
      await api.post<{ success: boolean }>('/billing/reactivate-subscription');
      await reloadAllData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reactivate subscription.';
      setSeatsError(message);
    } finally {
      setReactivating(false);
    }
  };

  const handleSwitchToPlan = async () => {
    if (!viewPlanDetail) return;
    const targetPrice = viewPlanDetail.prices.find((p) => p.interval === modalInterval)
      ?? viewPlanDetail.prices[0];
    if (!targetPrice) return;

    setSwitchingPlan(true);
    setSeatsError(null);
    try {
      await api.patch<{ success: boolean }>('/billing/switch-price', {
        priceId: targetPrice.id,
      });
      await reloadAllData();
      await refreshMemberships();
      setViewPlanDetail(null);
      setConfirmNewPlan(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to switch plan.';
      setSeatsError(message);
    } finally {
      setSwitchingPlan(false);
    }
  };

  // For display
  const displayPrice = hasSubscription ? currentPrice : selectedPrice;
  const pricePerSeat = getPricePerSeat(displayPrice);
  const estimatedTotal = getEstimatedTotal(displayPrice, seats);

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-2xl md:mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/company/billing')}
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg
                     hover:bg-mist active:bg-stone transition-colors"
        >
          <ArrowLeft size={20} className="text-midnight" />
        </button>
        <div>
          <h1 className="font-display text-h1 text-midnight">Manage Plan</h1>
          <p className="text-small text-slate mt-0.5">
            {hasSubscription
              ? isFlatRate
                ? 'View your plan details or apply a coupon.'
                : 'Adjust users, switch billing, or apply coupons.'
              : 'Choose a plan and subscribe.'}
          </p>
        </div>
      </div>

      {/* Test card info banner (demo orgs only) */}
      {isDemoOrg && testCardNumber && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={18} className="text-amber-700" />
            <p className="text-small font-semibold text-amber-800">Stripe Test Card</p>
          </div>
          <p className="text-[13px] text-amber-700 mb-2 leading-relaxed">
            This is a test card number provided by Stripe for testing in the Sandbox/Test
            environment. It will not result in a real charge.
          </p>
          <div className="bg-white border border-amber-200 rounded-lg px-3 py-2.5 mb-2">
            <p className="font-mono text-body tracking-wider text-midnight text-center select-all">
              {testCardNumber.replace(/(.{4})/g, '$1 ').trim()}
            </p>
          </div>
          <div className="flex flex-col gap-0.5 text-[12px] text-amber-700">
            <span><span className="font-semibold">Expiration date:</span> Any date in the future</span>
            <span><span className="font-semibold">Security code (CVC):</span> Any three digits</span>
          </div>
        </div>
      )}

      {/* Payment method banner */}
      {!loadingBilling && hasSubscription && paymentMethod === null && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3">
          <AlertCircle size={18} className="shrink-0 text-amber-600 mt-0.5" />
          <div>
            <p className="text-small font-semibold text-amber-800">
              No payment method on file
            </p>
            <p className="text-[13px] text-amber-700 mt-1 leading-relaxed">
              Your subscription doesn't have a payment method configured.
              Please{' '}
              <button
                type="button"
                onClick={() => navigate('/admin/company/billing/payments')}
                className="underline font-medium text-amber-800"
              >
                add a payment method
              </button>{' '}
              to avoid service interruption.
            </p>
          </div>
        </div>
      )}

      {/* Data load error banner */}
      {!loadingBilling && loadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
          <AlertCircle size={18} className="shrink-0 text-red-600 mt-0.5" />
          <div>
            <p className="text-small font-semibold text-red-800">
              Stripe data unavailable
            </p>
            <p className="text-[13px] text-red-700 mt-1 leading-relaxed">
              {loadError}
            </p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loadingBilling && (
        <div className="space-y-4 animate-pulse">
          {/* Current plan skeleton */}
          <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
            <div className="h-4 w-32 bg-stone/40 rounded mb-3" />
            <div className="h-6 w-48 bg-stone/40 rounded mb-2" />
            <div className="h-3 w-64 bg-stone/30 rounded" />
          </div>
          {/* Users skeleton */}
          <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
            <div className="h-4 w-20 bg-stone/40 rounded mb-3" />
            <div className="flex items-center justify-center gap-4">
              <div className="w-11 h-11 bg-stone/30 rounded-lg" />
              <div className="h-8 w-12 bg-stone/40 rounded" />
              <div className="w-11 h-11 bg-stone/30 rounded-lg" />
            </div>
          </div>
          {/* Available plans skeleton */}
          <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
            <div className="h-4 w-36 bg-stone/40 rounded mb-3" />
            <div className="space-y-2">
              <div className="h-16 bg-stone/20 rounded-lg" />
              <div className="h-16 bg-stone/20 rounded-lg" />
            </div>
          </div>
        </div>
      )}

      {/* ── Cancellation Pending Banner ─────────────────────────────── */}
      {!loadingBilling && hasSubscription && billing?.cancelAtPeriodEnd && (
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-brand-sm mb-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="shrink-0 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-small font-semibold text-amber-800">
                Your plan is set to cancel
              </p>
              <p className="text-[13px] text-amber-700 mt-1 leading-relaxed">
                Your subscription will remain active until{' '}
                <span className="font-semibold">
                  {billing.currentPeriodEnd
                    ? new Date(billing.currentPeriodEnd * 1000).toLocaleDateString()
                    : 'the end of your billing period'}
                </span>.
                After that, you'll lose access to the app. Your data will be preserved and available if you re-subscribe.
              </p>
              <button
                type="button"
                onClick={handleReactivateSubscription}
                disabled={reactivating || dataUnavailable}
                className="btn btn-primary mt-3 flex items-center gap-2"
              >
                {reactivating ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Reactivating…
                  </>
                ) : (
                  <>
                    <RotateCcw size={14} />
                    Keep My Subscription
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Current Plan Summary ─────────────────────────────────────── */}
      {!loadingBilling && hasSubscription && (
        <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <CreditCard size={18} className="text-plum" />
            <h2 className="font-display text-h3 text-midnight">
              {billing?.productName || currentProduct?.name || 'Current Plan'}
            </h2>
            {currentPrice?.unitAmount === 0 && (
              <span className="text-[11px] font-semibold uppercase text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">
                Free
              </span>
            )}
          </div>

          {/* Pricing name and description */}
          <div className="mb-3">
            {(billing?.priceNickname || currentPrice?.nickname) && (
              <p className="text-[13px] text-plum font-medium mt-0.5">
                {billing?.priceNickname || currentPrice?.nickname}
              </p>
            )}
            {currentProduct?.description && (
              <p className="text-[13px] text-slate mt-0.5 leading-snug">{currentProduct.description}</p>
            )}
          </div>

          {/* Trial period banner */}
          {billing?.status === 'trialing' && (
            <div className="bg-plum/5 border border-plum/20 rounded-lg px-3 py-2 mb-3 flex items-center gap-2">
              <Clock size={14} className="shrink-0 text-plum" />
              <p className="text-[13px] text-midnight">
                <span className="font-semibold text-plum">Trial period active</span>
                {billing.trialEnd ? (
                  <>{' — '}<span className="font-medium">{daysUntil(billing.trialEnd)} days</span> remaining. Billing begins {new Date(billing.trialEnd * 1000).toLocaleDateString()}.</>
                ) : (
                  <>{' — '}your subscription will begin billing at the end of the trial.</>
                )}
              </p>
            </div>
          )}

          {/* Pricing details */}
          {isFlatRate ? (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="text-center">
                <DollarSign size={16} className="mx-auto text-plum mb-1" />
                <p className={`font-mono text-[22px] font-medium leading-none ${currentPrice?.unitAmount === 0 ? 'text-green-700' : 'text-midnight'}`}>
                  {currentPrice?.unitAmount === 0 ? 'Free' : `$${(currentPrice?.unitAmount ?? 0) / 100}`}
                </p>
                <p className="text-[12px] text-slate mt-0.5">
                  {currentPrice?.unitAmount === 0 ? 'no charge' : `flat rate / ${intervalLabel(currentInterval)}`}
                </p>
              </div>
              <div className="text-center">
                <Users size={16} className="mx-auto text-plum mb-1" />
                <p className="font-mono text-[22px] font-medium text-midnight leading-none">
                  {memberCount?.totalCount ?? 0}
                </p>
                <p className="text-[12px] text-slate mt-0.5">active users</p>
              </div>
            </div>
          ) : currentPrice?.billingScheme === 'tiered' && currentPrice.tiers ? (
            <div className="mb-3">
              <p className="text-[12px] text-slate uppercase tracking-wider font-semibold mb-1">
                {currentPrice.tiersMode === 'volume' ? 'Volume' : 'Graduated'} tiered pricing
              </p>
              {currentPrice.tiers.map((tier, i) => (
                <div key={i} className="text-[13px] text-midnight flex items-baseline gap-1">
                  <span className="font-mono text-plum font-medium">{formatCents(tier.unitAmount)}</span>
                  <span className="text-slate">/user/{intervalLabel(currentInterval)}</span>
                  <span className="text-slate ml-1">
                    {tier.upTo === null
                      ? `(above ${i > 0 ? currentPrice.tiers![i - 1].upTo : 0} users)`
                      : i === 0
                        ? `(first ${tier.upTo} users)`
                        : `(${(currentPrice.tiers![i - 1]?.upTo ?? 0) + 1}–${tier.upTo} users)`}
                  </span>
                </div>
              ))}
              <div className="mt-2 bg-mist rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-small text-slate">
                  Current total ({billing?.subscriptionQuantity ?? 0} users)
                </span>
                <span className="font-mono text-body font-medium text-midnight">
                  {estimatedTotal !== null ? `$${getEstimatedTotal(currentPrice, billing?.subscriptionQuantity ?? 0)?.toFixed(2)}/${intervalLabel(currentInterval)}` : '—'}
                </span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center">
                <Users size={16} className="mx-auto text-plum mb-1" />
                <p className="font-mono text-[22px] font-medium text-midnight leading-none">
                  {billing?.subscriptionQuantity ?? 0}
                </p>
                <p className="text-[12px] text-slate mt-0.5">users</p>
              </div>
              <div className="text-center">
                <DollarSign size={16} className="mx-auto text-plum mb-1" />
                <p className="font-mono text-[22px] font-medium text-midnight leading-none">
                  {pricePerSeat !== null ? `$${pricePerSeat}` : '—'}
                </p>
                <p className="text-[12px] text-slate mt-0.5">per user/{intervalLabel(currentInterval)}</p>
              </div>
              <div className="text-center">
                <CreditCard size={16} className="mx-auto text-plum mb-1" />
                <p className="font-mono text-[22px] font-medium text-midnight leading-none">
                  {estimatedTotal !== null ? `$${getEstimatedTotal(currentPrice, billing?.subscriptionQuantity ?? 0)?.toFixed(2)}` : '—'}
                </p>
                <p className="text-[12px] text-slate mt-0.5">total/{intervalLabel(currentInterval)}</p>
              </div>
            </div>
          )}

          {/* Billing period switcher */}
          {alternativePrice && currentPrice?.unitAmount !== 0 && (
            <div className="border-t border-stone pt-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <ArrowRightLeft size={14} className="text-plum" />
                <p className="text-[13px] font-semibold text-midnight">Switch Billing Period</p>
              </div>
              <p className="text-[12px] text-slate mb-2 leading-relaxed">
                Currently <span className="font-semibold text-midnight">{intervalLabelFull(currentInterval)}</span>.
                Switch to <span className="font-semibold text-midnight">{intervalLabelFull(alternativeInterval)}</span>{' '}
                {alternativePrice.billingScheme !== 'tiered' && (
                  <>at <span className="font-mono font-medium text-midnight">
                    {alternativePrice.unitAmount !== null ? formatCents(alternativePrice.unitAmount) : '—'}
                  </span>{isFlatRate ? '' : '/user'}/{intervalLabel(alternativeInterval)}</>
                )}.
                {alternativeInterval === 'year' && ' Save by switching to annual.'}
              </p>
              <button
                type="button"
                onClick={() => setConfirmSwitch(true)}
                disabled={switchingPrice || dataUnavailable}
                className="btn-secondary w-full text-[13px] flex items-center justify-center gap-2"
              >
                {switchingPrice ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Switching…
                  </>
                ) : switchSuccess ? (
                  <>
                    <CheckCircle2 size={14} /> Switched!
                  </>
                ) : (
                  `Switch to ${intervalLabelFull(alternativeInterval)}`
                )}
              </button>
            </div>
          )}

          {/* ── Users sub-section (hidden for flat-rate) ────────────── */}
          {!isFlatRate && (
            <div className="border-t border-stone pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Hash size={14} className="text-plum" />
                <p className="text-[13px] font-semibold text-midnight">Users</p>
              </div>
              <p className="text-[12px] text-slate mb-3 leading-relaxed">
                Each user is a license for one team member. Changing user count adjusts your billing automatically.
              </p>

              {/* Member count info */}
              {memberCount && memberCount.totalCount > 0 && (
                <div className="bg-mist rounded-lg px-3 py-2 mb-3 flex items-center justify-between">
                  <span className="text-[12px] text-slate">Active users (active + invited)</span>
                  <span className="font-mono text-small font-medium text-midnight">
                    {memberCount.totalCount}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-4 mb-3">
                <button
                  type="button"
                  onClick={handleDecrementSeats}
                  disabled={!canDecrement}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-stone
                             hover:border-plum/40 active:bg-mist disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors"
                >
                  <Minus size={16} className="text-midnight" />
                </button>

                <div className="flex-1 text-center">
                  <p className="font-mono text-[28px] font-medium text-midnight leading-none">
                    {seats}
                  </p>
                  <p className="text-[12px] text-slate mt-1">
                    {seats === 1 ? 'user' : 'users'}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleIncrementSeats}
                  disabled={dataUnavailable}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-stone
                             hover:border-plum/40 active:bg-mist disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors"
                >
                  <Plus size={16} className="text-midnight" />
                </button>
              </div>

              {/* Seat floor warning */}
              {isBelowFloor && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 flex items-start gap-2">
                  <AlertCircle size={14} className="shrink-0 text-amber-600 mt-0.5" />
                  <p className="text-[12px] text-amber-700 leading-relaxed">
                    You have {memberCount?.totalCount} users (active and invited).
                    Remove users before reducing below this number.
                  </p>
                </div>
              )}

              {seatsError && (
                <div className="flex items-start gap-2 text-[13px] text-red-600 mb-3">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>{seatsError}</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => setConfirmSeats(true)}
                disabled={seatsSaving || isBelowFloor || dataUnavailable}
                className="btn btn-primary w-full text-[13px]"
              >
                {seatsSaving ? 'Updating…' : seatsSuccess ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle2 size={14} /> Updated
                  </span>
                ) : (
                  'Update Users'
                )}
              </button>

              {seatsSuccess && (
                <p className="text-[12px] text-green-600 mt-2 text-center">
                  User count updated. Billing will be adjusted on your next invoice.
                </p>
              )}
            </div>
          )}

          {/* Cancel Plan link (inside current plan card) */}
          {!billing?.cancelAtPeriodEnd && (
            <div className="border-t border-stone pt-3 mt-3">
              <button
                type="button"
                onClick={() => setConfirmCancel(true)}
                disabled={canceling || dataUnavailable}
                className="w-full flex items-center justify-center gap-2 py-2 text-[13px]
                           font-medium text-slate hover:text-red-600 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircle size={14} />
                Cancel Plan
              </button>
            </div>
          )}
        </section>
      )}

      {/* ── Available Plans (unified — shown for both new and existing subscribers) */}
      {!loadingBilling && plans.length > 0 && (
        <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Package size={18} className="text-plum" />
            <h2 className="font-display text-h3 text-midnight">Available Plans</h2>
          </div>
          <p className="text-small text-slate mb-3">
            {hasSubscription
              ? 'Explore other plans. Switching takes effect immediately — prorated charges or credits apply to your next invoice.'
              : 'Choose a plan to get started. You can change your plan at any time.'}
          </p>

          <div className="space-y-2">
            {plans.map((plan) => {
              const monthlyPrice = plan.prices.find((p) => p.interval === 'month');
              const annualPrice = plan.prices.find((p) => p.interval === 'year');
              const displayP = monthlyPrice ?? annualPrice ?? plan.prices[0];
              if (!displayP) return null;
              const isPlanFlatRate = displayP.isFlatRate;
              const isFreePrice = displayP.unitAmount === 0;
              const isCurrent = hasSubscription && plan.id === billing?.productId;

              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => {
                    if (!hasSubscription) setSelectedProductId(plan.id);
                    setViewPlanDetail(plan);
                    setModalInterval(monthlyPrice ? 'month' : 'year');
                  }}
                  disabled={dataUnavailable}
                  className={`w-full text-left rounded-lg border-2 p-3 transition-colors
                              disabled:opacity-50 disabled:cursor-not-allowed
                              ${isCurrent
                                ? 'border-plum/30 bg-plum/5'
                                : 'border-stone hover:border-plum/40 bg-white'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display text-[15px] font-semibold text-midnight">
                          {plan.name}
                        </p>
                        {isCurrent && (
                          <span className="text-[11px] font-semibold uppercase text-plum bg-plum/10 border border-plum/20 rounded-full px-1.5 py-0.5">
                            Current
                          </span>
                        )}
                        {isFreePrice && (
                          <span className="text-[11px] font-semibold uppercase text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">
                            Free
                          </span>
                        )}
                      </div>
                      {plan.description && (
                        <p className="text-[12px] text-slate mt-0.5 leading-snug line-clamp-2">{plan.description}</p>
                      )}
                      {plan.trialDays && plan.trialDays > 0 && !isFreePrice && (
                        <p className="text-[11px] text-plum font-medium mt-1 flex items-center gap-1">
                          <Clock size={11} className="shrink-0" />
                          {plan.trialDays}-day free trial
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      {isFreePrice ? (
                        <p className="font-mono text-[18px] font-medium text-green-700">$0</p>
                      ) : isPlanFlatRate ? (
                        <>
                          <p className="font-mono text-[18px] font-medium text-midnight">
                            {displayP.unitAmount !== null ? formatCents(displayP.unitAmount) : '—'}
                          </p>
                          <p className="text-[11px] text-slate">/{intervalLabel(displayP.interval)}</p>
                        </>
                      ) : displayP.billingScheme === 'tiered' && displayP.tiers ? (
                        <>
                          <p className="text-[12px] text-slate">
                            From {formatCents(displayP.tiers[0].unitAmount)}
                          </p>
                          <p className="text-[11px] text-slate">/user/{intervalLabel(displayP.interval)}</p>
                        </>
                      ) : (
                        <>
                          <p className="font-mono text-[18px] font-medium text-midnight">
                            {displayP.unitAmount !== null ? formatCents(displayP.unitAmount) : '—'}
                          </p>
                          <p className="text-[11px] text-slate">/user/{intervalLabel(displayP.interval)}</p>
                        </>
                      )}
                      {/* Show alternative interval price */}
                      {annualPrice && monthlyPrice && (
                        <p className="text-[10px] text-slate/70 mt-0.5">
                          or {formatCents(annualPrice.unitAmount ?? 0)}/{intervalLabel('year')}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Coupon Code (for existing subscriptions, all plan types) ── */}
      {!loadingBilling && hasSubscription && (
        <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Tag size={16} className="text-plum" />
            <h2 className="font-display text-h3 text-midnight">Coupon Code</h2>
          </div>

          <p className="text-small text-slate mb-3">
            Have a coupon or promo code? Enter it below to apply a discount to your subscription.
          </p>

          <div className="flex gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter code"
              disabled={dataUnavailable}
              className="flex-1 min-w-0 px-3 py-2.5 rounded-lg border border-stone
                         bg-white text-small text-midnight font-mono placeholder:text-slate
                         focus:outline-none focus:ring-2 focus:ring-plum/40 focus:border-plum
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              onClick={handleApplyCoupon}
              disabled={couponSaving || !couponCode.trim() || dataUnavailable}
              className="btn btn-primary px-5 shrink-0"
            >
              {couponSaving ? 'Applying…' : 'Apply'}
            </button>
          </div>

          {couponResult && (
            <div
              className={`mt-3 flex items-start gap-2 text-small ${
                couponResult.ok ? 'text-green-600' : 'text-amber-600'
              }`}
            >
              {couponResult.ok ? (
                <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
              ) : (
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
              )}
              <span>{couponResult.message}</span>
            </div>
          )}
        </section>
      )}

      {/* ── Confirmation Modals ─────────────────────────────────────── */}
      <ConfirmModal
        isOpen={confirmSeats}
        title="Update Users"
        message={`Are you sure you want to change your user count to ${seats}? Your billing will be adjusted automatically on your next invoice.`}
        confirmLabel="Confirm Update"
        onConfirm={handleSaveSeats}
        onClose={() => setConfirmSeats(false)}
      />

      {alternativePrice && (
        <ConfirmModal
          isOpen={confirmSwitch}
          title="Switch Billing Period"
          message={`Are you sure you want to switch from ${intervalLabelFull(currentInterval)} to ${intervalLabelFull(alternativeInterval)} billing? The change will take effect on your next billing cycle.`}
          confirmLabel={`Switch to ${intervalLabelFull(alternativeInterval)}`}
          onConfirm={handleSwitchPrice}
          onClose={() => setConfirmSwitch(false)}
        />
      )}

      <ConfirmModal
        isOpen={confirmCancel}
        title="Cancel Your Plan?"
        message={
          billing?.currentPeriodEnd
            ? `Your subscription will remain active until ${new Date(billing.currentPeriodEnd * 1000).toLocaleDateString()}. After that date, you'll lose access to the app. Your data will NOT be deleted — everything will be exactly as you left it if you choose to re-subscribe later.`
            : 'Your subscription will remain active until the end of your current billing period. After that, you\'ll lose access to the app. Your data will NOT be deleted — everything will be exactly as you left it if you choose to re-subscribe later.'
        }
        confirmLabel="Yes, Cancel at Period End"
        variant="danger"
        onConfirm={handleCancelSubscription}
        onClose={() => setConfirmCancel(false)}
      />

      <ConfirmModal
        isOpen={confirmNewPlan}
        title="Switch Plan?"
        message={viewPlanDetail
          ? `Are you sure you want to switch to ${viewPlanDetail.name}? The change takes effect immediately. Any prorated charges or credits will be applied to your next invoice.`
          : ''}
        confirmLabel={switchingPlan ? 'Switching…' : 'Confirm Switch'}
        onConfirm={handleSwitchToPlan}
        onClose={() => { setConfirmNewPlan(false); }}
      />

      {/* ── Plan Detail Modal ──────────────────────────────────────── */}
      {viewPlanDetail && !confirmNewPlan && (() => {
        const modalPlanIntervals = [...new Set(viewPlanDetail.prices.map((p) => p.interval))];
        const modalPrice = viewPlanDetail.prices.find((p) => p.interval === modalInterval)
          ?? viewPlanDetail.prices[0];
        const modalIsFree = modalPrice?.unitAmount === 0;
        const modalIsFlatRate = !!(modalPrice?.isFlatRate);
        const isCurrent = hasSubscription && viewPlanDetail.id === billing?.productId;
        const isCurrentPrice = isCurrent && modalPrice?.id === billing?.priceId;
        // Coupon entry only makes sense before a subscription exists and on paid plans.
        const modalShowsCoupon = !hasSubscription && !modalIsFree;
        const modalCouponExcluded = !!modalAppliedCoupon && couponExcludesProduct(modalAppliedCoupon, viewPlanDetail.id);
        const modalCouponUsable = !!modalAppliedCoupon && !modalCouponExcluded;
        const modalDiscountedAmount = modalCouponUsable && modalPrice?.unitAmount
          ? applyCouponToCents(modalPrice.unitAmount, modalAppliedCoupon)
          : null;

        return (
          <div
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="absolute inset-0 bg-midnight/70"
              onClick={() => setViewPlanDetail(null)}
              aria-hidden="true"
            />
            <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-6 flex flex-col gap-4 max-h-[85vh] overflow-y-auto">
              {/* Close button */}
              <button
                type="button"
                onClick={() => setViewPlanDetail(null)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg
                           hover:bg-mist transition-colors"
              >
                <X size={18} className="text-slate" />
              </button>

              {/* Plan header */}
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-display text-h2 text-midnight">{viewPlanDetail.name}</h2>
                  {isCurrent && (
                    <span className="text-[11px] font-semibold uppercase text-plum bg-plum/10 border border-plum/20 rounded-full px-1.5 py-0.5">
                      Current
                    </span>
                  )}
                  {modalIsFree && (
                    <span className="text-[11px] font-semibold uppercase text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">
                      Free
                    </span>
                  )}
                </div>
                {viewPlanDetail.description && (
                  <p className="text-[13px] text-slate mt-1 leading-relaxed">{viewPlanDetail.description}</p>
                )}
                {viewPlanDetail.trialDays && viewPlanDetail.trialDays > 0 && !modalIsFree && (
                  <p className="text-[13px] text-plum font-medium mt-2 flex items-center gap-1">
                    <Clock size={13} className="shrink-0" />
                    {viewPlanDetail.trialDays}-day free trial
                  </p>
                )}
                {viewPlanDetail.maxUsers !== null && viewPlanDetail.maxUsers !== undefined && (
                  <p className="text-[12px] text-slate mt-1">
                    Up to <span className="font-semibold text-midnight">{viewPlanDetail.maxUsers}</span> users
                  </p>
                )}
              </div>

              {/* Pricing interval selector */}
              {modalPlanIntervals.length > 1 && (
                <div className="flex gap-2">
                  {modalPlanIntervals.includes('month') && (
                    <button
                      type="button"
                      onClick={() => setModalInterval('month')}
                      className={`flex-1 py-2 rounded-lg text-[13px] font-medium border transition-colors
                                  ${modalInterval === 'month'
                                    ? 'bg-plum text-white border-plum'
                                    : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                    >
                      Monthly
                    </button>
                  )}
                  {modalPlanIntervals.includes('year') && (
                    <button
                      type="button"
                      onClick={() => setModalInterval('year')}
                      className={`flex-1 py-2 rounded-lg text-[13px] font-medium border transition-colors
                                  ${modalInterval === 'year'
                                    ? 'bg-plum text-white border-plum'
                                    : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                    >
                      Annual
                    </button>
                  )}
                </div>
              )}

              {/* Selected price display */}
              {modalPrice && (
                <div className="bg-mist rounded-xl p-4 text-center">
                  {modalDiscountedAmount !== null && modalPrice.unitAmount !== null ? (
                    <>
                      <p className="font-mono text-[15px] text-slate line-through leading-none">
                        {formatCents(modalPrice.unitAmount)}
                      </p>
                      <p className="font-mono text-[28px] font-medium leading-none text-green-700 mt-1">
                        {formatCents(modalDiscountedAmount)}
                      </p>
                    </>
                  ) : (
                    <p className={`font-mono text-[28px] font-medium leading-none ${modalIsFree ? 'text-green-700' : 'text-midnight'}`}>
                      {modalIsFree ? '$0' : modalPrice.unitAmount !== null ? formatCents(modalPrice.unitAmount) : '—'}
                    </p>
                  )}
                  {!modalIsFree && (
                    <p className="text-[12px] text-slate mt-1">
                      {modalIsFlatRate ? `flat rate` : 'per user'} / {intervalLabel(modalPrice.interval)}
                    </p>
                  )}
                  {modalPrice.nickname && (
                    <p className="text-[11px] text-plum font-medium mt-1">{modalPrice.nickname}</p>
                  )}
                </div>
              )}

              {/* Coupon code (new subscribers, paid plans) */}
              {modalShowsCoupon && (
                <div>
                  <p className="text-[12px] text-slate uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
                    <Tag size={12} className="text-plum" />
                    Coupon Code
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={modalCouponCode}
                      onChange={(e) => setModalCouponCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleValidateModalCoupon(); } }}
                      placeholder="Enter code"
                      aria-label="Coupon code"
                      className="flex-1 min-w-0 h-11 px-3 rounded-lg border border-stone
                                 bg-white text-small text-midnight font-mono placeholder:text-slate
                                 focus:outline-none focus:ring-2 focus:ring-plum/40 focus:border-plum
                                 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={handleValidateModalCoupon}
                      disabled={modalCouponSaving || !modalCouponCode.trim()}
                      className="btn btn-secondary h-11 px-4 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {modalCouponSaving ? 'Checking…' : 'Apply'}
                    </button>
                  </div>
                  {modalCouponResult && (
                    <div
                      className={`mt-2 flex items-start gap-2 text-[13px] ${
                        modalCouponResult.ok && !modalCouponExcluded ? 'text-green-600' : 'text-amber-600'
                      }`}
                    >
                      {modalCouponResult.ok && !modalCouponExcluded ? (
                        <CheckCircle2 size={15} className="shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle size={15} className="shrink-0 mt-0.5" />
                      )}
                      <span>
                        {modalCouponExcluded
                          ? `Coupon "${modalAppliedCoupon?.couponName}" is valid but does not apply to ${viewPlanDetail.name}.`
                          : modalCouponResult.message}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Features list */}
              {viewPlanDetail.features && Object.keys(viewPlanDetail.features).length > 0 && (
                <div>
                  <p className="text-[12px] text-slate uppercase tracking-wider font-semibold mb-2">Features Included</p>
                  <div className="space-y-1.5">
                    {Object.entries(viewPlanDetail.features).map(([key, enabled]) => (
                      <div key={key} className="flex items-center gap-2">
                        {enabled ? (
                          <CheckCircle2 size={14} className="shrink-0 text-green-600" />
                        ) : (
                          <XCircle size={14} className="shrink-0 text-slate/40" />
                        )}
                        <span className={`text-[13px] ${enabled ? 'text-midnight' : 'text-slate/50 line-through'}`}>
                          {FEATURE_LABELS[key] ?? key}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info text */}
              <div className="flex items-start gap-2 text-[12px] text-slate">
                <Info size={14} className="shrink-0 mt-0.5 text-plum" />
                <p className="leading-relaxed">
                  {hasSubscription
                    ? 'Switching plans takes effect immediately. Prorated charges or credits appear on your next invoice.'
                    : 'You\'ll be redirected to Stripe to complete your subscription securely.'}
                </p>
              </div>

              {/* Action button */}
              {hasSubscription ? (
                isCurrentPrice ? (
                  <div className="bg-mist rounded-lg px-4 py-3 text-center">
                    <p className="text-sm text-slate font-medium">This is your current plan</p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmNewPlan(true)}
                    disabled={dataUnavailable}
                    className="btn btn-primary w-full flex items-center justify-center gap-2
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Switch to {viewPlanDetail.name}
                  </button>
                )
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      const price = viewPlanDetail.prices.find((p) => p.interval === modalInterval)
                        ?? viewPlanDetail.prices[0];
                      if (!price) return;
                      setSelectedProductId(viewPlanDetail.id);
                      setSelectedInterval(modalInterval);
                      const couponForCheckout = modalCouponUsable ? modalAppliedCoupon?.code : undefined;
                      setViewPlanDetail(null);
                      handleStartCheckout(price.id, couponForCheckout);
                    }}
                    disabled={dataUnavailable}
                    className="btn btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {modalIsFree ? 'Start Free Plan' : `Subscribe to ${viewPlanDetail.name}`}
                  </button>
                  {viewPlanDetail.trialDays && viewPlanDetail.trialDays > 0 && !modalIsFree && (
                    <p className="text-[12px] text-plum text-center flex items-center justify-center gap-1">
                      <Clock size={12} />
                      Includes a {viewPlanDetail.trialDays}-day free trial
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
